import { pipe } from "remeda"

import type { EmailAttachment } from "@productlane/api"
import { populateEmailAttachments } from "@productlane/api/src/lib/email"
import { convertHtmlToText } from "@productlane/lib/src/convert-html"
import {
	addTargetBlankToLinks,
	isForwardedText,
	linkAndStripStyles,
	splitEmailChunks,
} from "@productlane/lib/src/html"

import { lruCached } from "./lru-cache"

// cheerio, node-html-parser and html-to-text each reparse the whole body, and a
// synced email can be megabytes, so every derived value is computed once per
// body instead of once per render. The limit holds two 30-message detail
// pages, so switching between two threads stays warm.
const CACHE_LIMIT = 64

// Hiding signature images needs its own parse of the unpopulated body. Past this
// size those two passes cost more than the few icons they drop.
const SIGNATURE_SCAN_LIMIT = 512 * 1024

export interface EmailBodyChunks {
	visible: string
	hidden: string
}

export interface EmailSignatureInfo {
	hidden: string
	cids: ReadonlySet<string>
}

export interface EmailTextFlags {
	hasText: boolean
	isForwarded: boolean
}

const EMPTY_CHUNKS: EmailBodyChunks = { visible: "", hidden: "" }
const EMPTY_SIGNATURE: EmailSignatureInfo = { hidden: "", cids: new Set() }
const EMPTY_FLAGS: EmailTextFlags = { hasText: false, isForwarded: false }

function cached<T>(cache: Map<string, T>, key: string, compute: () => T): T {
	return lruCached(cache, CACHE_LIMIT, key, compute)
}

const chunksCache = new Map<string, EmailBodyChunks>()
const signatureCache = new Map<string, EmailSignatureInfo>()
const flagsCache = new Map<string, EmailTextFlags>()

const attachmentsKey = (attachments: EmailAttachment[]) =>
	attachments
		.map((a) => `${a.ContentID ?? ""}:${a.Content?.slice(0, 64) ?? ""}`)
		.join("|")

export function emailBodyChunks(
	html: string | null | undefined,
	attachments: EmailAttachment[],
): EmailBodyChunks {
	if (!html) return EMPTY_CHUNKS
	return cached(chunksCache, `${attachmentsKey(attachments)} ${html}`, () => {
		const [visible, hidden] = pipe(
			html,
			populateEmailAttachments(attachments),
			addTargetBlankToLinks,
			splitEmailChunks,
		)
		return { visible, hidden }
	})
}

export function emailSignatureInfo(
	html: string | null | undefined,
): EmailSignatureInfo {
	if (!html || html.length > SIGNATURE_SCAN_LIMIT) return EMPTY_SIGNATURE
	return cached(signatureCache, html, () => {
		const [, hidden] = splitEmailChunks(linkAndStripStyles(html))
		if (!hidden) return EMPTY_SIGNATURE
		const cids = new Set<string>()
		const cidPattern = /cid:([^"'\s>]+)/g
		let match
		while ((match = cidPattern.exec(hidden)) !== null) {
			if (match[1]) cids.add(match[1])
		}
		return { hidden, cids }
	})
}

export function emailTextFlags(
	html: string | null | undefined,
): EmailTextFlags {
	if (!html) return EMPTY_FLAGS
	return cached(flagsCache, html, () => {
		const text = convertHtmlToText(html)
		return {
			hasText: text.trim().length > 0,
			isForwarded: isForwardedText(text),
		}
	})
}

export function clearEmailBodyCaches() {
	chunksCache.clear()
	signatureCache.clear()
	flagsCache.clear()
}

interface WithContent {
	content?: { htmlContent: string; textContent: string } | null
}

/** An email's body, which only the thread detail queries load; elsewhere it reads as empty. */
export function emailBody(email: object | null | undefined) {
	const content = (email as WithContent | null | undefined)?.content
	return {
		html: content?.htmlContent ?? "",
		text: content?.textContent ?? "",
	}
}
